/**
 * Theme run details page — /reports/themes/:jobId
 *
 * Follows the Cloudscape details-page pattern: page title is the resource ID,
 * global actions in the header, a details summary container (key-value pairs),
 * and a related-resources container with the run's results. Polls while the
 * run is active, so deep links to an in-progress run show live progress.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import { useNavigate, useParams } from 'react-router-dom';
import { cancelThemeJob, getThemeJob } from '../api/tickets';
import { RootCauseTheme, ThemeJob } from '../types/ticket';
import {
  POLL_INTERVAL_MS,
  StatusBadge,
  absoluteTime,
  isActive,
  progressText,
  severitiesText,
  themesToCsv,
  windowText,
} from '../components/reports/themeRunShared';
import { recordEvent } from '../rum';

export function ThemeRunDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<ThemeJob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRef = useRef(true);

  const load = useCallback(() => {
    if (!jobId) return;
    getThemeJob(jobId)
      .then((next) => {
        if (!activeRef.current) return;
        setJob(next);
        setLoading(false);
        if (isActive(next.status)) {
          pollRef.current = setTimeout(load, POLL_INTERVAL_MS);
        }
      })
      .catch((e: any) => {
        if (!activeRef.current) return;
        setLoading(false);
        setError(e.message);
      });
  }, [jobId]);

  useEffect(() => {
    activeRef.current = true;
    load();
    return () => {
      activeRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [load]);

  const handleCancel = async () => {
    if (!jobId) return;
    recordEvent('theme_job_cancel', { jobId });
    try {
      await cancelThemeJob(jobId);
      if (pollRef.current) clearTimeout(pollRef.current);
      load();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleThemeClick = (theme: RootCauseTheme) => {
    const params = new URLSearchParams();
    params.set('ticketIds', theme.ticketIds.join(','));
    params.set('themeTitle', theme.title);
    if (jobId) params.set('themeRun', jobId); // lets breadcrumbs link back to this run
    navigate(`/tickets?${params.toString()}`);
  };

  const THEME_COLUMNS = [
    {
      id: 'title',
      header: 'Root cause',
      cell: (item: RootCauseTheme) => (
        <Link onFollow={(e) => { e.preventDefault(); handleThemeClick(item); }}>{item.title}</Link>
      ),
    },
    { id: 'count', header: 'Tickets', cell: (item: RootCauseTheme) => item.ticketCount, width: 100 },
    { id: 'explanation', header: 'What broke / fix direction', cell: (item: RootCauseTheme) => item.explanation },
  ];

  if (loading) {
    return (
      <Box textAlign="center" padding="xxl">
        <Spinner size="large" />
      </Box>
    );
  }

  if (!job) {
    return (
      <Alert
        type="error"
        header="Run not found"
        action={<Button onClick={() => navigate('/reports?tab=root-cause-themes')}>Back to Root Cause Themes</Button>}
      >
        {error || 'This theme run does not exist or has expired (runs are kept for 7 days).'}
      </Alert>
    );
  }

  const result = job.status === 'SUCCEEDED' ? job.result : undefined;
  const shortId = job.jobId.slice(0, 8);

  return (
    <SpaceBetween size="m">
      <Header
        variant="h1"
        description="On-demand AI analysis clustering tickets into specific, actionable root causes."
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              iconName="download"
              onClick={() => themesToCsv(job)}
              disabled={!result || result.themes.length === 0}
            >
              Download CSV
            </Button>
            {isActive(job.status) && (
              <Button iconName="close" onClick={handleCancel} disabled={job.status === 'CANCELLING'}>
                {job.status === 'CANCELLING' ? 'Cancelling' : 'Cancel run'}
              </Button>
            )}
          </SpaceBetween>
        }
      >
        Theme run {shortId}
      </Header>

      {error && <Alert type="error">{error}</Alert>}

      <Container header={<Header variant="h2">Run configuration</Header>}>
        <KeyValuePairs
          columns={4}
          items={[
            { label: 'Status', value: <StatusBadge status={job.status} /> },
            { label: 'Resolver group', value: job.params?.assignedGroup || 'All groups' },
            { label: 'Time window', value: windowText(job.params) },
            { label: 'Severities', value: severitiesText(job.params) },
            { label: 'Started', value: absoluteTime(job.createdAt) },
            { label: 'Model', value: result?.model || '—' },
            { label: 'Tickets analyzed', value: result ? String(result.totalTickets) : '—' },
            { label: 'Themes found', value: result ? String(result.themes.length) : '—' },
          ]}
        />
      </Container>

      {isActive(job.status) && (
        <Container>
          <Box textAlign="center" padding="l">
            <SpaceBetween size="s" alignItems="center">
              <Spinner size="large" />
              <StatusIndicator type="in-progress">
                {job.status === 'CANCELLING' ? 'Cancelling' : progressText(job)}
              </StatusIndicator>
              <Box variant="small" color="text-body-secondary">
                This can take a few minutes. You can leave this page — the run continues in the background.
              </Box>
            </SpaceBetween>
          </Box>
        </Container>
      )}

      {job.status === 'FAILED' && (
        <Alert type="error" header="Run failed">{job.error || 'Theme generation failed.'}</Alert>
      )}
      {job.status === 'CANCELLED' && <Alert type="warning">This run was cancelled.</Alert>}

      {result && (
        <Container
          header={
            <Header variant="h2" description="Select a root cause to view its tickets.">
              Results
            </Header>
          }
        >
          <Table
            items={result.themes}
            columnDefinitions={THEME_COLUMNS}
            variant="embedded"
            wrapLines
            trackBy="title"
            empty={
              <Box color="text-body-secondary" padding="l" textAlign="center">
                No tickets matched the selected group and window.
              </Box>
            }
          />
        </Container>
      )}
    </SpaceBetween>
  );
}
